import React, { useState, useEffect, useRef } from "react";
import { SendHorizonal, MessageCircle, X } from "lucide-react";

const GeminiChat = () => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const idleTimerRef = useRef(null);

  const BASE_URL = "https://api.circuitcytechnology.com";

  // Auto scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chatRef.current && !chatRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-close on idle
  useEffect(() => {
    if (!isOpen) return;
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setIsOpen(false), 30000);
    };
    resetIdleTimer();
    const events = ["keydown", "click"];
    events.forEach((event) => document.addEventListener(event, resetIdleTimer));
    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach((event) => document.removeEventListener(event, resetIdleTimer));
    };
  }, [isOpen]);

  const sendMessage = async () => {
    if (!userInput.trim()) return;

    const userMessage = { from: "user", text: userInput };
    setMessages((prev) => [...prev, userMessage]);
    setUserInput("");
    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/gemini`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: userInput }),
      });

      const data = await response.json();
      const aiReply = data.reply || "⚠️ Sorry, I couldn't respond.";

      const aiMessage = { from: "ai", text: aiReply };
      setMessages((prev) => [...prev, aiMessage]);

      // Optional: send transcript if AI suggests contact
      if (/email|contact/i.test(aiReply)) {
        await fetch(`${BASE_URL}/submit-form`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            transcript: [...messages, userMessage, aiMessage]
              .map((m) => `${m.from}: ${m.text}`)
              .join("\n"),
          }),
        });
      }
    } catch (err) {
      console.error("Gemini fetch error:", err);
      setMessages((prev) => [
        ...prev,
        { from: "ai", text: "⚠️ Something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            if (messages.length === 0) {
              setMessages([{ from: "ai", text: "👋 Hi! What course are you pursuing?" }]);
            }
          }}
          className="fixed bottom-6 right-6 bg-sky-950 hover:bg-sky-700 text-white p-4 rounded-full shadow-xl z-50"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {isOpen && (
        <div
          ref={chatRef}
          className="fixed bottom-6 right-6 w-80 sm:w-96 bg-[#0d1117] text-white border border-sky-500 rounded-2xl shadow-2xl z-50 flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-600 via-teal-500 to-sky-400 px-4 py-3 flex justify-between items-center rounded-t-2xl">
            <h2 className="font-semibold">Circuitcy Chat</h2>
            <button onClick={() => setIsOpen(false)} className="text-white hover:text-gray-300">
              <X size={18} />
            </button>
          </div>

          {/* Chat Area */}
          <div className="h-72 overflow-y-auto px-4 py-3 bg-[#161b22] text-sm flex flex-col gap-2">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`w-full flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`px-4 py-2 rounded-xl max-w-[75%] break-words ${
                    msg.from === "user"
                      ? "bg-sky-600 text-white rounded-br-none"
                      : "bg-[#0d1117] text-white border border-sky-500 rounded-bl-none"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-left text-sky-300 text-xs italic">Typing...</div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex items-center bg-[#0d1117] border-t border-gray-700 p-3 gap-2">
            <input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type here..."
              className="flex-1 px-3 py-2 bg-[#161b22] text-white border border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
            <button
              onClick={sendMessage}
              disabled={loading}
              className="bg-sky-500 hover:bg-sky-600 text-white p-2 rounded-full"
            >
              <SendHorizonal size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GeminiChat;
